import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

import { environment } from '../../../../environments/environment';
import { KiwixiService } from '../kiwixi/kiwixi.service';
import { KiwixiGlobals } from '../kiwixi/kiwixi.globals';
import { MaterialService } from './material.service';
import {
  LegalDocument,
  LegalDocumentAcceptance,
  AcceptLegalDocumentPayload
} from '../model/legal.model';

/**
 * Service de gestion des documents légaux (CGU, déclaration sur l'honneur)
 * Source: api-recurring/recurring/legal/documentation/how_to.md
 */
@Injectable()
export class LegalService extends KiwixiService {
  private readonly API_BASE = `${environment.server}`;

  constructor(
    private _http: HttpClient,
    private _router: Router,
    kiwixiGlobals: KiwixiGlobals,
    private materialService: MaterialService,
  ) {
    super(_http, _router, kiwixiGlobals, materialService);
  }

  /**
   * Récupère les documents légaux pour un test LILATE
   * Source: api-recurring/recurring/legal/documentation/how_to.md#GET /api/lilate_exams/{pk}/legal-documents/
   */
  getLegalDocumentsForLilate(lilateId: number): Observable<LegalDocument[]> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<LegalDocument[]>(
      `${this.API_BASE}lilate_exams/${lilateId}/legal-documents/`,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<LegalDocument[]>('getLegalDocumentsForLilate'))
    );
  }

  /**
   * Récupère les documents légaux pour un test ELATE
   * Source: api-recurring/recurring/legal/documentation/how_to.md#GET /api/elates/{pk}/legal-documents/
   */
  getLegalDocumentsForElate(elateId: number): Observable<LegalDocument[]> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<LegalDocument[]>(
      `${this.API_BASE}elates/${elateId}/legal-documents/`,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<LegalDocument[]>('getLegalDocumentsForElate'))
    );
  }

  /**
   * Récupère les IDs des documents légaux déjà acceptés pour ce test Lilate.
   * GET /api/lilate_exams/{pk}/legal-acceptance/
   * Réponse : { accepted_document_ids: number[] }
   */
  getLegalAcceptanceStatusForLilate(lilateId: number): Observable<{ accepted_document_ids: number[] }> {
    const headers = this.createHttpClientAuthorizationHeader();
    return this._http.get<{ accepted_document_ids: number[] }>(
      `${this.API_BASE}lilate_exams/${lilateId}/legal-acceptance/`,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<{ accepted_document_ids: number[] }>('getLegalAcceptanceStatusForLilate'))
    );
  }

  /**
   * Accepte un document légal pour un test LILATE
   * Source: api-recurring/recurring/legal/documentation/how_to.md#POST /api/lilate_exams/{pk}/legal-acceptance/
   */
  acceptDocumentForLilate(lilateId: number, payload: AcceptLegalDocumentPayload): Observable<LegalDocumentAcceptance> {
    const headers = this.createHttpClientAuthorizationHeaderNoEnctype();
    return this._http.post<LegalDocumentAcceptance>(
      `${this.API_BASE}lilate_exams/${lilateId}/legal-acceptance/`,
      payload,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<LegalDocumentAcceptance>('acceptDocumentForLilate'))
    );
  }

  /**
   * Accepte un document légal pour un test ELATE
   * Source: api-recurring/recurring/legal/documentation/how_to.md#POST /api/elates/{pk}/legal-acceptance/
   */
  acceptDocumentForElate(elateId: number, payload: AcceptLegalDocumentPayload): Observable<LegalDocumentAcceptance> {
    const headers = this.createHttpClientAuthorizationHeaderNoEnctype();
    return this._http.post<LegalDocumentAcceptance>(
      `${this.API_BASE}elates/${elateId}/legal-acceptance/`,
      payload,
      { headers }
    ).pipe(
      catchError(this.handleErrorObservable<LegalDocumentAcceptance>('acceptDocumentForElate'))
    );
  }

  // -------------------------------------------------------------------
  // Méthodes externes FlashLevel (par token, sans auth)
  // -------------------------------------------------------------------

  /**
   * Récupère les documents légaux pour un FlashLevel par token (sans auth)
   * GET /api/external/v1/flashlevel/{token}/legal-documents/
   */
  getLegalDocumentsForFlashLevel(token: string): Observable<LegalDocument[]> {
    return this._http.get<LegalDocument[]>(
      `${environment.base_server}/api/external/v1/flashlevel/${token}/legal-documents/`
    ).pipe(
      catchError(this.handleErrorObservable<LegalDocument[]>('getLegalDocumentsForFlashLevel'))
    );
  }

  /**
   * Récupère les IDs des documents légaux déjà acceptés pour ce FlashLevel par token (sans auth)
   * GET /api/external/v1/flashlevel/{token}/legal-acceptance/
   */
  getLegalAcceptanceStatusForFlashLevel(token: string): Observable<{ accepted_document_ids: number[] }> {
    return this._http.get<{ accepted_document_ids: number[] }>(
      `${environment.base_server}/api/external/v1/flashlevel/${token}/legal-acceptance/`
    ).pipe(
      catchError(this.handleErrorObservable<{ accepted_document_ids: number[] }>('getLegalAcceptanceStatusForFlashLevel'))
    );
  }

  /**
   * Accepte un document légal pour un FlashLevel par token (sans auth)
   * POST /api/external/v1/flashlevel/{token}/legal-acceptance/
   */
  acceptDocumentForFlashLevel(token: string, payload: AcceptLegalDocumentPayload): Observable<LegalDocumentAcceptance> {
    return this._http.post<LegalDocumentAcceptance>(
      `${environment.base_server}/api/external/v1/flashlevel/${token}/legal-acceptance/`,
      payload
    ).pipe(
      catchError(this.handleErrorObservable<LegalDocumentAcceptance>('acceptDocumentForFlashLevel'))
    );
  }
}
